// PreviewTester/components/TemplateRow.js - NATIVE HTML5 DRAG & DROP

import React, { useState, useRef } from 'react';

const TemplateRow = ({
  row,
  index,
  isEditMode,
  onDelete,
  onMoveDrop,
  onDragStart,
  onDragOver,
  onDrop,
  onContentChange,
  availableVariables = [],
  totalRows,
  onAddButtonToGroup,      // NEW
  onDeleteButtonFromGroup, // NEW
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(row?.content || '');
  const [showVariablePicker, setShowVariablePicker] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  if (!row) return null;

  const getRowTypeInfo = () => {
    const typeMap = {
      'for_loop_start': { label: 'FOR', color: '#4299e1', bg: '#ebf8ff' },
      'for_loop_end': { label: 'END FOR', color: '#4299e1', bg: '#ebf8ff' },
      'if_start': { label: 'IF', color: '#ed8936', bg: '#fffaf0' },
      'elif': { label: 'ELIF', color: '#ed8936', bg: '#fffaf0' },
      'else': { label: 'ELSE', color: '#ed8936', bg: '#fffaf0' },
      'if_end': { label: 'END IF', color: '#ed8936', bg: '#fffaf0' },
      'variable': { label: 'VAR', color: '#48bb78', bg: '#f0fff4' },
      'custom': { label: '✏️', color: '#9f7aea', bg: '#faf5ff' },
      'button': { label: '🔘', color: '#48bb78', bg: '#f0fff4' },
      'button-group': { label: '📦', color: '#ed8936', bg: '#fefcbf' },
      'comment': { label: '💬', color: '#a0aec0', bg: '#f7fafc' },
      'empty': { label: '␣', color: '#a0aec0', bg: '#f7fafc' },
      'text': { label: 'TXT', color: '#4a5568', bg: '#edf2f7' },
    };
    return typeMap[row.type] || typeMap['text'];
  };
  const typeInfo = getRowTypeInfo();

  const handleContentChange = (e) => setEditContent(e.target.value);
  
  const handleCancelEdit = () => { 
    setEditContent(row.content); 
    setIsEditing(false); 
    setShowVariablePicker(false); 
  };
  
  const handleDelete = () => { 
    if (onDelete) onDelete(row.id); 
  };
  
  const handleSaveEdit = () => { 
    if (onContentChange) onContentChange(row.id, editContent); 
    setIsEditing(false); 
    setShowVariablePicker(false); 
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { 
      e.preventDefault(); 
      handleSaveEdit(); 
    }
    if (e.key === 'Escape') handleCancelEdit();
    if (e.key === '@') { 
      setCursorPosition(inputRef.current?.selectionStart || 0); 
      setShowVariablePicker(true); 
    }
  };

  const handleVariableSelect = (variable) => {
    const before = editContent.substring(0, cursorPosition);
    const after = editContent.substring(cursorPosition);
    const newContent = before + `{{ ${variable} }}` + after;
    setEditContent(newContent);
    setShowVariablePicker(false);
    setTimeout(() => {
      if (inputRef.current) {
        const newPos = cursorPosition + `{{ ${variable} }}`.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 10);
  };

  const getRowStyle = () => {
    let style = {
      display: 'flex', 
      alignItems: 'center', 
      padding: '4px 8px', 
      borderRadius: '4px',
      backgroundColor: 'white', 
      border: '1px solid #e2e8f0', 
      transition: 'all 0.2s',
      fontFamily: 'monospace', 
      fontSize: '13px', 
      position: 'relative', 
      minHeight: '32px', 
      width: '100%',
    };
    if (isDragOver) style = { ...style, borderColor: '#4299e1', borderWidth: '2px' };
    if (row.isCustom) style = { ...style, backgroundColor: '#faf5ff', borderColor: '#9f7aea' };
    if (row.type === 'for_loop_start' || row.type === 'for_loop_end') style = { ...style, backgroundColor: '#ebf8ff', borderColor: '#bee3f8' };
    if (row.type === 'if_start' || row.type === 'elif' || row.type === 'else' || row.type === 'if_end') style = { ...style, backgroundColor: '#fffaf0', borderColor: '#fbd38d' };
    if (row.type === 'button-group') style = { ...style, backgroundColor: '#fefcbf', borderColor: '#ed8936', borderWidth: '2px' };
    return style;
  };

  // ============================================================
  // RENDER BUTTON GROUP ROW
  // ============================================================
  if (row.type === 'button-group') {
    return (
      <div
        ref={containerRef}
        draggable={isEditMode}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', index.toString());
          if (onDragStart) onDragStart(e, index);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setIsDragOver(true);
          if (onDragOver) onDragOver(e, index);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          if (onDrop) onDrop(e, index);
          if (onMoveDrop) {
            const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
            if (!isNaN(dragIndex) && dragIndex !== index) onMoveDrop(dragIndex, index);
          }
        }}
        style={{
          ...getRowStyle(),
          cursor: isEditMode ? 'grab' : 'default',
          paddingLeft: `${row.depth * 16 + 8}px`,
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        {/* Header row with drag handle and controls */}
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <div style={{ 
            cursor: 'grab', 
            padding: '4px 8px', 
            color: '#a0aec0', 
            fontSize: '14px', 
            userSelect: 'none', 
            flexShrink: 0 
          }} title="Drag to reorder">⋮</div>
          
          <span style={{ 
            padding: '1px 6px', 
            borderRadius: '10px', 
            fontSize: '9px', 
            fontWeight: '600', 
            backgroundColor: '#fefcbf', 
            color: '#ed8936', 
            marginRight: '8px', 
            flexShrink: 0, 
            fontFamily: 'sans-serif' 
          }}>
            📦 GROUP
          </span>
          
          <span style={{ fontWeight: '600', color: '#744210', fontSize: '12px' }}>
            {row.content}
          </span>
          
          <span style={{ fontSize: '10px', color: '#718096', marginLeft: '8px' }}>
            ({row.buttons?.length || 0} buttons)
          </span>
          
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
            {!isEditing && (
              <button 
                onClick={() => setIsEditing(true)} 
                style={{ 
                  padding: '2px 6px', 
                  backgroundColor: 'transparent', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '4px', 
                  cursor: 'pointer', 
                  fontSize: '11px', 
                  color: '#4a5568' 
                }} 
                title="Edit group name"
              >
                ✎
              </button>
            )}
            <button 
              onClick={handleDelete} 
              style={{ 
                padding: '2px 6px', 
                backgroundColor: 'transparent', 
                border: '1px solid #e2e8f0', 
                borderRadius: '4px', 
                cursor: 'pointer', 
                fontSize: '11px', 
                color: '#fc8181' 
              }} 
              title="Delete this group"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Editing group name */}
        {isEditing && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            padding: '4px 8px 4px 32px',
            marginTop: '4px',
          }}>
            <input 
              ref={inputRef}
              value={editContent} 
              onChange={handleContentChange} 
              onKeyDown={handleKeyDown}
              style={{ 
                flex: 1, 
                padding: '4px 8px', 
                border: '2px solid #ed8936', 
                borderRadius: '4px', 
                fontSize: '13px', 
                fontFamily: 'monospace', 
                outline: 'none', 
                backgroundColor: 'white' 
              }}
              autoFocus 
              placeholder="Enter group name..." 
            />
            <button 
              onClick={handleSaveEdit} 
              style={{ 
                padding: '2px 10px', 
                backgroundColor: '#48bb78', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer', 
                fontSize: '11px', 
                fontWeight: '500' 
              }}
            >
              ✓
            </button>
            <button 
              onClick={handleCancelEdit} 
              style={{ 
                padding: '2px 10px', 
                backgroundColor: '#fc8181', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer', 
                fontSize: '11px', 
                fontWeight: '500' 
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Buttons inside the group */}
        {row.buttons && row.buttons.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            padding: '6px 12px 6px 32px',
            marginTop: '4px',
          }}>
            {row.buttons.map((btn, idx) => (
              <div key={btn.id || idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: 'white',
                padding: '2px 8px 2px 12px',
                borderRadius: '4px',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.borderColor = '#ed8936'}
              onMouseLeave={(e) => e.target.style.borderColor = '#e2e8f0'}
              >
                <span style={{ fontSize: '12px' }}>{btn.text}</span>
                {isEditMode && (
                  <button
                    onClick={() => onDeleteButtonFromGroup && onDeleteButtonFromGroup(row.id, btn.id)}
                    style={{
                      padding: '2px 4px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#fc8181',
                      cursor: 'pointer',
                      fontSize: '10px',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#e53e3e'}
                    onMouseLeave={(e) => e.target.style.color = '#fc8181'}
                    title="Remove button"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add button inside group */}
        {isEditMode && (
          <button
            onClick={() => onAddButtonToGroup && onAddButtonToGroup(row.id)}
            style={{
              margin: '4px 0 0 32px',
              padding: '2px 10px',
              backgroundColor: 'transparent',
              color: '#48bb78',
              border: '1px dashed #48bb78',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '10px',
              alignSelf: 'flex-start',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0fff4';
              e.target.style.borderStyle = 'solid';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.borderStyle = 'dashed';
            }}
          >
            + Add Button
          </button>
        )}
      </div>
    );
  }

  // ============================================================
  // RENDER REGULAR ROW
  // ============================================================
  if (!isEditMode) {
    return (
      <div style={getRowStyle()} ref={containerRef}>
        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: `${row.depth * 16}px` }}>
          <div dangerouslySetInnerHTML={{ __html: row.content || ' ' }} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      draggable={isEditMode}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
        if (onDragStart) onDragStart(e, index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
        if (onDragOver) onDragOver(e, index);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (onDrop) onDrop(e, index);
        if (onMoveDrop) {
          const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
          if (!isNaN(dragIndex) && dragIndex !== index) onMoveDrop(dragIndex, index);
        }
      }}
      style={{
        ...getRowStyle(),
        cursor: isEditMode ? 'grab' : 'default',
        paddingLeft: `${row.depth * 16 + 8}px`,
      }}
    >
      <div style={{ cursor: 'grab', padding: '4px 8px', color: '#a0aec0', fontSize: '14px', userSelect: 'none', flexShrink: 0 }} title="Drag to reorder">⋮</div>
      <span style={{ padding: '1px 6px', borderRadius: '10px', fontSize: '9px', fontWeight: '600', backgroundColor: typeInfo.bg, color: typeInfo.color, marginRight: '8px', flexShrink: 0, fontFamily: 'sans-serif' }}>{typeInfo.label}</span>

      {isEditing ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
          <input 
            ref={inputRef} 
            value={editContent} 
            onChange={handleContentChange} 
            onKeyDown={handleKeyDown}
            style={{ 
              flex: 1, 
              padding: '4px 8px', 
              border: '2px solid #4299e1', 
              borderRadius: '4px', 
              fontSize: '13px', 
              fontFamily: 'monospace', 
              outline: 'none', 
              backgroundColor: 'white' 
            }}
            autoFocus 
            placeholder="Enter template content..." 
          />
          {showVariablePicker && availableVariables?.length > 0 && (
            <div style={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              backgroundColor: 'white', 
              border: '1px solid #e2e8f0', 
              borderRadius: '4px', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
              zIndex: 1000, 
              maxHeight: '200px', 
              overflowY: 'auto', 
              minWidth: '200px' 
            }}>
              {availableVariables.map((v) => (
                <div 
                  key={v} 
                  style={{ 
                    padding: '6px 12px', 
                    cursor: 'pointer', 
                    fontSize: '13px', 
                    fontFamily: 'monospace', 
                    borderBottom: '1px solid #f7fafc' 
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f4ff'} 
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  onClick={() => handleVariableSelect(v)}
                >
                  {v}
                </div>
              ))}
            </div>
          )}
          <button 
            onClick={handleSaveEdit} 
            style={{ 
              padding: '2px 10px', 
              backgroundColor: '#48bb78', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              fontSize: '11px', 
              fontWeight: '500' 
            }}
          >
            ✓
          </button>
          <button 
            onClick={handleCancelEdit} 
            style={{ 
              padding: '2px 10px', 
              backgroundColor: '#fc8181', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              fontSize: '11px', 
              fontWeight: '500' 
            }}
          >
            ✕
          </button>
        </div>
      ) : (
        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <div dangerouslySetInnerHTML={{ __html: row.content || ' ' }} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} />
        </div>
      )}

      <div style={{ display: 'flex', gap: '4px', flexShrink: 0, marginLeft: '8px' }}>
        {!isEditing && (
          <button 
            onClick={() => setIsEditing(true)} 
            style={{ 
              padding: '2px 6px', 
              backgroundColor: 'transparent', 
              border: '1px solid #e2e8f0', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              fontSize: '11px', 
              color: '#4a5568' 
            }} 
            title="Edit this row"
          >
            ✎
          </button>
        )}
        {!row.isLoopStart && !row.isLoopEnd && (
          <button 
            onClick={handleDelete} 
            style={{ 
              padding: '2px 6px', 
              backgroundColor: 'transparent', 
              border: '1px solid #e2e8f0', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              fontSize: '11px', 
              color: '#fc8181' 
            }} 
            title="Delete this row"
          >
            ✕
          </button>
        )}
        {row.isCustom && (
          <span style={{ 
            fontSize: '10px', 
            color: '#9f7aea', 
            padding: '2px 6px', 
            backgroundColor: '#faf5ff', 
            borderRadius: '10px' 
          }}>
            ✏️
          </span>
        )}
        {row.isButton && (
          <span style={{ 
            fontSize: '10px', 
            color: '#48bb78', 
            padding: '2px 6px', 
            backgroundColor: '#f0fff4', 
            borderRadius: '10px' 
          }}>
            🔘
          </span>
        )}
      </div>
    </div>
  );
};

export default TemplateRow;